import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { Variants } from "@dhl-official/stencil-library";
// eslint-disable-next-line import/no-cycle
import { Label, Placeholder, ValidationFeedback } from "./shared";
import {
  RIGHT_ICON_OFFSET,
  inputFieldBorderStyles,
  StyledIconWrapper,
  disabledStyles,
} from "./styled";

export const GaQInputFieldTypes = {
  EMAIL: "email",
  NUMBER: "number",
  NUMERIC: "numeric",
  PASSWORD: "password",
  TELEPHONE: "tel",
  TEXT: "text",
  DATE: "date",
};

// #region Component Styles

const Container = styled.div`
  display: ${({ isBlock }) => (isBlock ? "block" : "inline-block")};
  position: relative;
  width: ${({ isBlock }) => (isBlock ? "100%" : "200px")};
`;

const Input = styled.input.attrs({
  noValidate: true,
})`
  ${({ isShowingAnyIcon, hasLabel, value, shouldHidePlaceholder }) => {
    return css`
      ${inputFieldBorderStyles};

      font-family: var(--dui-font-family);
      font-size: var(--dui-size-font-md);
      outline: 0;

      padding: ${
        hasLabel
          ? "calc(var(--dui-size-space-12x) + var(--dui-size-space-1)) var(--dui-size-space-16x) var(--dui-size-space-5x) var(--dui-size-space-7x)"
          : "var(--dui-size-space-10x) var(--dui-size-space-6x) var(--dui-size-space-4x) var(--dui-size-space-6x)"
      };

      padding-right: ${isShowingAnyIcon ? RIGHT_ICON_OFFSET : undefined};

      width: 100%;

      ::placeholder {
        opacity: ${shouldHidePlaceholder ? 0 : 1};
        color: var(--dui-color-gray-400);
      }

      ${
        value &&
        css`
          ~ ${Label} {
            font-size: var(--dui-size-font-xs);
            opacity: 1;
            transform: translateY(0);
          }

          ~ ${Placeholder} {
            opacity: 0;
          }
        `
      };


      &:hover {
        border-color: var(--dui-color-black-500);
      }

      &:focus-within {
        box-shadow: var(--dui-size-space-0) var(--dui-size-space-0)
            var(--dui-size-space-0) var(--dui-size-space-1)
            var(--dui-color-black-500);
      }

      &:focus {
        appearance: none;
        border-color: var(--dui-color-black-500) !important;
        box-shadow: var(--dui-size-space-0) var(--dui-size-space-0)
            var(--dui-size-space-0) var(--dui-size-space-1)
            var(--dui-color-black-500);

        ~ ${Label} {
          font-size: var(--dui-size-font-xs);
          opacity: 1;
          transform: translateY(0);
          font-weight: 700;
        }

        ~ ${Placeholder} {
          font-size: var(--dui-size-font-xs);
          opacity: 0;
          transform: translateY(-7px);
        }
      }

      &:disabled {
        ${disabledStyles.container}

        ~ ${Label},
        ~ ${Placeholder},
        ~ i,
        ::placeholder {
          ${disabledStyles.items}
        }
      }

      &[type="date"] {
        background: none;
        height: 56px;
        padding-right: var(--dui-size-space-4x);
        /* here we need to have in pixels */
        position: relative;
        &::-webkit-calendar-picker-indicator {
          border: solid var(--dui-size-width-border-xs) transparent;
          padding: var(--gaq-size-space-7px);
          position: absolute;
          right: 7px;
          top: 20%;
          &:focus {
            border-color: var(--dui-color-black-500);
            border-radius: var(--dui-size-radius-md);
          }
        }
      }
    `;
  }}
`;
// #endregion

export const shouldShowIcon = (icon, isValid, isInvalid) =>
  // when the right icon exists AND there is no competing validation icon to display
  !!icon && !isValid && !isInvalid;

const concatIfSet = (values) => values.filter(Boolean).join(" ") || undefined;

const getValidationMessageId = (fieldId) => `${fieldId}_ValidationMessage`;

const GaqInputField = ({
  className,
  id,
  dataTestid,
  /**
   * Code smell:
   *
   * The default export for `GaqInputField` already supports the `ref` attribute and the `forwardedRef`
   * is just an implementation detail that the consumer doesn't need to know as she can use the standard `ref`.
   * */
  // eslint-disable-next-line react/prop-types
  forwardedRef,
  isBlock,
  onBlur,
  onChange,
  type,
  validation,
  value,
  variant,
  rightIcon,
  ariaLabel,
  ariaDescribedBy,
  ariaHaspopup,
  ariaExpanded,
  required,
  name,
  ...autoCompleteProps
}) => {
  const { type: variantType, placeholder, label } = variant;
  const hasLabel = Boolean(label);
  const isAnimated = variantType === Variants.animated;
  const isInvalid = validation?.type === Variants.invalid;
  const isValid = validation?.type === Variants.valid;

  const showRightIcon = shouldShowIcon(rightIcon, isValid, isInvalid);
  const { searchIcon } = autoCompleteProps;

  const isShowingAnyIcon = showRightIcon || isValid || isInvalid;

  const combinedAriaDescribedBy = concatIfSet([
    ariaDescribedBy,
    validation?.message && getValidationMessageId(id),
  ]);

  return (
    <Container className={className} isBlock={isBlock}>
      <Input
        aria-describedby={combinedAriaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHaspopup}
        aria-label={ariaLabel}
        data-testid={dataTestid}
        hasLabel={!searchIcon && hasLabel}
        id={id}
        isBlock={isBlock}
        isInvalid={isInvalid}
        isShowingAnyIcon={isShowingAnyIcon}
        isValid={isValid}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        ref={forwardedRef}
        required={required}
        type={type}
        value={value}
        placeholder={placeholder}
        // We hide (opacity: 0) the DOM placeholder for animated placeholder as we rely
        // on a `span` element to act as a placeholder for the animation. The reason
        // we still want to placeholder attribute here is because screen readers will announce it
        // properly.
        shouldHidePlaceholder={variantType === Variants.animated}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...autoCompleteProps}
      />

      {/* Label should not be visible if input is a search field */}
      {!autoCompleteProps.searchIcon && label && (
        <Label
          id={`${id}-label`}
          isInvalid={isInvalid}
          isRequired={required}
          shouldHide={isAnimated && Boolean(placeholder)}
          variantType={variantType}
          htmlFor={id}
        >
          {label}
        </Label>
      )}

      {showRightIcon && (
        <StyledIconWrapper block size="regular">
          {rightIcon}
        </StyledIconWrapper>
      )}

      {isAnimated && placeholder && (
        <Placeholder
          /* This placeholder is aria-hidden because the "real" input field's placeholder is what
           * we want the screen readers to read. This `span` element is here only for styling purposes as
           * it floats around when the users focuses on the input field.
           *
           * E.g.:
           * 1. Users set an `animated` or `static` placeholder as "My cool placeholder"
           * 2. Underlying rendered DOM will be <input type="text" placeholder="My cool placeholder" />
           * 3. For animated placeholder, we will also render this `span` element here that looks visually as
           * a placeholder but it animates when the user focuses on the `input` field. Therefore we set it to
           * `aria-hidden` as the real announced placeholder will be already the `placeholder` attribute from `input`.
           * 4. For static placeholder we rely only on the input's placeholder attribute so it is all fine.
           */
          aria-hidden
          hasRightIcon={isShowingAnyIcon}
          hasLeftIcon={searchIcon}
          isRequired={required}
        >
          {placeholder}
        </Placeholder>
      )}
      {validation && (
        <ValidationFeedback validation={validation} fieldId={id} />
      )}
    </Container>
  );
};

GaqInputField.propTypes = {
  /** A **required** id prop for the Input component  */
  id: PropTypes.string.isRequired,
  /** An **optional** classname prop for the Input component */
  className: PropTypes.string,
  /** An **optional** test-id prop used to target the Input component for testing */
  dataTestid: PropTypes.string,
  /** An **optional** prop flag to control if the Input is rendered as block or inline-block element */
  isBlock: PropTypes.bool,
  /** An **optional** onBlur callback handler */
  onBlur: PropTypes.func,
  /** An **optional** onChange callback handler */
  onChange: PropTypes.func,
  /** An **optional** prop describing the type of the Input, e.g. text or password input */
  type: PropTypes.oneOf([
    GaQInputFieldTypes.EMAIL,
    GaQInputFieldTypes.NUMBER,
    GaQInputFieldTypes.NUMERIC,
    GaQInputFieldTypes.PASSWORD,
    GaQInputFieldTypes.TELEPHONE,
    GaQInputFieldTypes.TEXT,
    GaQInputFieldTypes.DATE,
  ]),
  /** An **optional** Input validation prop
   *
   * **Required Fields** `type`
   */
  validation: PropTypes.shape({
    /** Validation message */
    message: PropTypes.string,
    /** Validation type */
    type: PropTypes.oneOf([Variants.valid, Variants.invalid, Variants.note])
      .isRequired,
  }),
  /** An **optional** prop holding the current input value */
  value: PropTypes.string,
  /** An **optional** Input variant prop - follows the same structure as InputField
   *
   * **Required Fields** `type`
   */
  variant: PropTypes.shape({
    label: PropTypes.string,
    placeholder: PropTypes.string,
    type: PropTypes.oneOf([Variants.animated, Variants.static]).isRequired,
  }),
  /** An **optional** Icon prop to display to the right of the textfield, e.g. `<InputField rightIcon={<Icon/>} />` */
  rightIcon: PropTypes.element,
  /** An **optional** prop flag to define if the Input is disabled */
  disabled: PropTypes.bool,
  /** An **optional** prop flag to define if the search icon is shown next to input */
  searchIcon: PropTypes.bool,
  /** An **optional** prop defining the text read by the screen reader to represent the Input; use this if you need different text to be read from label */
  ariaLabel: PropTypes.string,
  /** An **optional** prop defining the list of reference ids (separated by space), recommended when you want to show some error message on your field */
  ariaDescribedBy: PropTypes.string,
  /** An **optional** prop that lets the screen reader know that this input has a popup */
  ariaHaspopup: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** An **optional** prop that lets the screen reader know that this input has a popup which is expanded or not - **optional** */
  ariaExpanded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** An **optional** prop to flag the Input component as required within a form context */
  required: PropTypes.bool,
  /** An **optional** name prop for the Input component */
  name: PropTypes.string,
};

GaqInputField.defaultProps = {
  value: "",
  isBlock: false,
  onBlur: () => {},
  onChange: () => {},
  type: Variants.text,
  variant: {
    type: Variants.static,
  },
  disabled: false,
  searchIcon: false,
};

/**
 * The `ref` property is required when this component is used by `react-autocomplete`.
 * It makes a reference to the `input` field inside the component and could also be
 * used to trigger `input.focus` for example.
 * */
const ForwardedRef = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <GaqInputField {...props} forwardedRef={ref} />
));

ForwardedRef.displayName = GaqInputField.displayName;
ForwardedRef.propTypes = GaqInputField.propTypes;
ForwardedRef.defaultProps = GaqInputField.defaultProps;

export default ForwardedRef;
